
import { useEffect, useState } from 'react';
import { supabase } from '../../utils/supabase';
import { featuresData } from '../../mocks/features';

interface BlogPost {
  slug: string;
  updated_at: string;
}

const SitemapPage = () => {
  const [sitemapXml, setSitemapXml] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const baseUrl = 'https://tgweb.com.br';
  const currentDate = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const generateSitemap = async () => {
      // URLs estáticas
      const staticUrls = [
        { loc: '/', priority: '1.0', changefreq: 'weekly' },
        { loc: '/funcionalidades', priority: '0.9', changefreq: 'weekly' },
        { loc: '/planos', priority: '0.9', changefreq: 'monthly' },
        { loc: '/blog', priority: '0.8', changefreq: 'daily' },
        { loc: '/faq', priority: '0.7', changefreq: 'weekly' },
      ];

      // URLs das funcionalidades
      const featureUrls = Object.keys(featuresData).map((slug) => ({
        loc: `/funcionalidades/${slug}`,
        priority: '0.8',
        changefreq: 'monthly',
      }));

      // URLs dos posts do blog (do Supabase)
      let blogUrls: { loc: string; priority: string; changefreq: string; lastmod?: string }[] = [];
      try {
        const { data: posts } = await supabase
          .from('blog_posts')
          .select('slug, updated_at')
          .eq('published', true);

        if (posts) {
          blogUrls = posts.map((post: BlogPost) => ({
            loc: `/blog/${post.slug}`,
            priority: '0.7',
            changefreq: 'monthly',
            lastmod: post.updated_at ? post.updated_at.split('T')[0] : currentDate,
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar posts do blog:', error);
      }

      // Gerar XML
      const allUrls = [...staticUrls, ...featureUrls, ...blogUrls];

      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${allUrls
  .map(
    (url) => `  <url>
    <loc>${baseUrl}${url.loc}</loc>
    <lastmod>${url.lastmod || currentDate}</lastmod>
    <changefreq>${url.changefreq}</changefreq>
    <priority>${url.priority}</priority>
  </url>`
  )
  .join('\n')}
</urlset>`;

      setSitemapXml(xml);
      setLoading(false);
    };

    generateSitemap();
  }, [currentDate]);

  const handleDownload = () => {
    const blob = new Blob([sitemapXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(sitemapXml);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line text-2xl text-teal-600 animate-spin"></i>
          </div>
          <p className="text-gray-600">Gerando sitemap...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className="ri-sitemap-line text-xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-white">Sitemap XML</h1>
                  <p className="text-teal-100 text-sm">Mapa do site para indexação</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-copy-line"></i>
                  Copiar
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-teal-700 hover:bg-teal-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-line"></i>
                  Baixar XML
                </button>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="px-6 py-4 bg-teal-50 border-b border-teal-100">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                <i className="ri-information-line text-teal-600"></i>
              </div>
              <div className="text-sm text-teal-800">
                <p className="font-medium mb-1">Como usar este sitemap:</p>
                <ol className="list-decimal list-inside space-y-1 text-teal-700">
                  <li>Baixe o arquivo XML clicando no botão acima</li>
                  <li>Faça upload do arquivo na raiz do seu domínio (ex: tgweb.com.br/sitemap.xml)</li>
                  <li>Envie o sitemap no Google Search Console para acelerar a indexação</li>
                </ol>
              </div>
            </div>
          </div>

          {/* XML Content */}
          <div className="p-6">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono leading-relaxed">
              <code>{sitemapXml}</code>
            </pre>
          </div>

          {/* Stats */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-links-line text-gray-400"></i>
                </div>
                <span>
                  <strong className="text-gray-900">
                    {sitemapXml.split('<url>').length - 1}
                  </strong>{' '}
                  URLs indexadas
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-gray-400"></i>
                </div>
                <span>
                  Atualizado em <strong className="text-gray-900">{currentDate}</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Back link */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 text-sm font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar para o site
          </a>
        </div>
      </div>
    </div>
  );
};

export default SitemapPage;
